/**
 * BottomSheet Component
 * Mobile-friendly modal pattern (<768px uses bottom sheet, >=768px uses center modal)
 * FIX: Proper drag handle, expand/collapse with snap points, iOS scroll lock
 */

import { useEffect, useState, useRef } from 'react';

// B5: Haptic feedback helper (respects prefers-reduced-motion)
const triggerHaptic = (type = 'light') => {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  // B5: Respect prefers-reduced-motion
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  try {
    const duration = type === 'light' ? 10 : type === 'medium' ? 20 : 30;
    navigator.vibrate(duration);
  } catch (e) {
    // Silently fail if vibration not supported
  }
};

export default function BottomSheet({ isOpen, onClose, children, title, disableDrag = false, peekSummary = null }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sheetRef = useRef(null);
  const dragStartY = useRef(null);
  const dragStartHeight = useRef(null);
  const isDragging = useRef(false);
  const snapState = useRef(0); // MOBILE PRO: Track current snap point (0: 40vh, 1: 65vh, 2: 90vh)
  const previousActiveElement = useRef(null);

  // FIX: Proper scroll lock for iOS (body + html, position fixed)
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.documentElement.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Reset expanded state when sheet opens
  useEffect(() => {
    if (isOpen) {
      setIsExpanded(false);
      snapState.current = 0; // MOBILE PRO: Reset to collapsed (40vh) on open
    }
  }, [isOpen]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // A11y: Escape key closes sheet (must run unconditionally to avoid React "more hooks" error)
  useEffect(() => {
    if (!isOpen || !isMobile) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isMobile, onClose]);

  // A11y: Focus management (must run unconditionally to avoid React "more hooks" error)
  useEffect(() => {
    if (!isOpen || !isMobile) return;
    previousActiveElement.current = document.activeElement;
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen || !isMobile || !sheetRef.current) return;
    const root = sheetRef.current;
    const focusables = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const rafId = requestAnimationFrame(() => {
      try {
        if (first && typeof first.focus === 'function') first.focus();
      } catch (_) {}
    });
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab' || focusables.length === 0) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          try { last?.focus?.(); } catch (_) {}
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          try { first?.focus?.(); } catch (_) {}
        }
      }
    };
    root.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(rafId);
      root.removeEventListener('keydown', handleKeyDown);
      try {
        previousActiveElement.current?.focus?.();
      } catch (_) {}
    };
  }, [isOpen, isMobile]);

  if (!isOpen) return null;

  if (isMobile) {
    // FIX: Dedicated drag handle handlers (Pointer Events + touch fallback)
    const handleDragStart = (clientY) => {
      isDragging.current = true;
      dragStartY.current = clientY;
      // MOBILE PRO: Determine current snap state from height
      const currentHeight = sheetRef.current ? parseFloat(sheetRef.current.style.height) || 40 : 40;
      if (currentHeight < 52.5) snapState.current = 0; // < 52.5 = collapsed (40vh)
      else if (currentHeight < 77.5) snapState.current = 1; // 52.5-77.5 = half (65vh)
      else snapState.current = 2; // >= 77.5 = expanded (90vh)
      dragStartHeight.current = snapState.current === 0 ? 40 : snapState.current === 1 ? 65 : 90;
    };

    const handleDragMove = (clientY) => {
      if (!isDragging.current || dragStartY.current === null) return;
      
      const deltaY = dragStartY.current - clientY; // Positive = dragging up
      const viewportHeight = window.innerHeight;
      const deltaVh = (deltaY / viewportHeight) * 100;
      const newHeight = Math.max(35, Math.min(92, dragStartHeight.current + deltaVh));
      
      if (sheetRef.current) {
        sheetRef.current.style.height = `${newHeight}vh`;
        sheetRef.current.style.transition = 'none'; // Disable transition during drag
      }
    };

    const handleDragEnd = () => {
      if (!isDragging.current || dragStartY.current === null) return;
      
      isDragging.current = false;
      dragStartY.current = null;
      
      if (sheetRef.current) {
        sheetRef.current.style.transition = 'height 0.3s ease-out'; // Re-enable transition
        
        // WORLD-CLASS: Snap to nearest point (40vh collapsed, 90vh expanded) or close if dragged down far enough
        const currentHeight = parseFloat(sheetRef.current.style.height) || 40;
        const viewportHeight = window.innerHeight;
        const currentHeightPx = (currentHeight / 100) * viewportHeight;
        const dragDistance = dragStartHeight.current ? 
          ((dragStartHeight.current / 100) * viewportHeight) - currentHeightPx : 0;
        
        // If dragged down more than 150px from start, close the sheet
        if (dragDistance > 150 && dragStartHeight.current <= 45) {
          onClose();
          return;
        }
        
        // MOBILE PRO: Snap to nearest of 3 points (40vh, 65vh, 90vh)
        let snapTo;
        if (currentHeight < 52.5) {
          snapTo = 40; // Collapsed
          snapState.current = 0;
        } else if (currentHeight < 77.5) {
          snapTo = 65; // Half
          snapState.current = 1;
        } else {
          snapTo = 90; // Expanded
          snapState.current = 2;
        }
        sheetRef.current.style.height = `${snapTo}vh`;
        setIsExpanded(snapTo >= 65);
        triggerHaptic('light'); // BEYOND WORLD-CLASS: Haptic on snap change
      }
    };

    // WORLD-CLASS V2: Pointer Events - allow drag from header zone or handle
    // BEYOND WORLD-CLASS: Disable drag when financial modal is open (gesture contract)
    const handlePointerDown = (e) => {
      if (disableDrag) return; // Gesture contract: no drag when modal open
      const isHeaderZone = e.target.closest('[data-bottom-sheet-header]');
      // Don't start drag if clicking close button
      if (isHeaderZone && !e.target.closest('[data-bottom-sheet-close]')) {
        e.preventDefault();
        handleDragStart(e.clientY);
        sheetRef.current?.setPointerCapture(e.pointerId);
      }
    };

    const handlePointerMove = (e) => {
      if (isDragging.current) {
        e.preventDefault();
        handleDragMove(e.clientY);
      }
    };

    const handlePointerUp = () => {
      if (isDragging.current) {
        handleDragEnd();
        sheetRef.current?.releasePointerCapture();
      }
    };

    // WORLD-CLASS V2: Touch fallback - allow drag from header zone
    // BEYOND WORLD-CLASS: Disable drag when financial modal is open (gesture contract)
    const handleTouchStart = (e) => {
      if (disableDrag) return; // Gesture contract: no drag when modal open
      const isHeaderZone = e.target.closest('[data-bottom-sheet-header]');
      // Don't start drag if clicking close button
      if (isHeaderZone && !e.target.closest('[data-bottom-sheet-close]')) {
        e.preventDefault();
        handleDragStart(e.touches[0].clientY);
      }
    };

    const handleTouchMove = (e) => {
      if (isDragging.current) {
        e.preventDefault();
        handleDragMove(e.touches[0].clientY);
      }
    };

    const handleTouchEnd = () => {
      if (isDragging.current) {
        handleDragEnd();
      }
    };

    // MOBILE PRO: Toggle through snap points (40vh -> 65vh -> 90vh -> 40vh)
    const handleHandleClick = () => {
      const currentHeight = sheetRef.current ? parseFloat(sheetRef.current.style.height) || 40 : 40;
      let nextSnap;
      if (currentHeight < 52.5) {
        nextSnap = 65; // Collapsed -> Half
        snapState.current = 1;
      } else if (currentHeight < 77.5) {
        nextSnap = 90; // Half -> Expanded
        snapState.current = 2;
      } else {
        nextSnap = 40; // Expanded -> Collapsed
        snapState.current = 0;
      }
      setIsExpanded(nextSnap >= 65);
      if (sheetRef.current) {
        sheetRef.current.style.height = `${nextSnap}vh`;
        triggerHaptic('light'); // BEYOND WORLD-CLASS: Haptic on snap change
      }
    };

    return (
      <div 
        data-bottom-sheet-backdrop="true"
        style={styles.mobileOverlay} 
        onClick={onClose}
        role="presentation"
        aria-hidden={!isOpen}
      >
        <div 
          ref={sheetRef}
          data-bottom-sheet-root="true"
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? "bottom-sheet-title" : undefined}
          style={{
            ...styles.mobileSheet,
            height: `${snapState.current === 0 ? 40 : snapState.current === 1 ? 65 : 90}vh`, // MOBILE PRO: 3 snap points (40vh, 65vh, 90vh)
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* WORLD-CLASS V2: Header zone with handle, chevron, hint, and close */}
          <div 
            data-bottom-sheet-header="true"
            style={styles.mobileHeaderZone}
            onClick={(e) => {
              // Toggle expand/collapse on header tap (except close button)
              if (!e.target.closest('[data-bottom-sheet-close]')) {
                e.stopPropagation();
                handleHandleClick();
              }
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            {/* Handle bar + chevron + hint */}
            <div 
              data-bottom-sheet-handle="true"
              data-drag-handle="true"
              style={styles.handleZone}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div style={styles.handleBar} />
              <div style={styles.handleContent}>
                {!isExpanded && (
                  <span style={styles.handleHint}>Pull up for details</span>
                )}
                <span 
                  data-bottom-sheet-chevron="true"
                  style={{
                    ...styles.chevron,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▲
                </span>
              </div>
            </div>
            
            {/* B1: Peek summary row (only when collapsed) */}
            {!isExpanded && peekSummary && (
              <div style={styles.peekRow} data-bottom-sheet-peek="true">
                <span style={styles.peekLabel}>Details</span>
                <span style={styles.peekSummary}>{peekSummary}</span>
                <span 
                  data-bottom-sheet-chevron="true"
                  style={styles.peekChevron}
                >
                  ▲
                </span>
              </div>
            )}
            
            {/* Always show close button on mobile (with or without title) so every sheet has a visible close */}
            {title ? (
              <div style={styles.mobileHeader}>
                <h3 id="bottom-sheet-title" style={styles.mobileTitle}>{title}</h3>
                <button
                  data-bottom-sheet-close="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  style={styles.mobileCloseButton}
                  aria-label="Close"
                  type="button"
                >
                  ×
                </button>
              </div>
            ) : (
              <div style={styles.mobileHeaderCloseOnly}>
                <button
                  data-bottom-sheet-close="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  style={styles.mobileCloseButton}
                  aria-label="Close"
                  type="button"
                >
                  ×
                </button>
              </div>
            )}
          </div>
          
          <div style={styles.mobileContent}>
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Center modal for desktop
  return (
    <div style={styles.desktopOverlay} onClick={onClose}>
      <div style={styles.desktopModal} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div style={styles.desktopHeader}>
            <h3 style={styles.desktopTitle}>{title}</h3>
            <button
              onClick={onClose}
              style={styles.desktopCloseButton}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <div style={styles.desktopContent}>
          {children}
        </div>
      </div>
    </div>
  );
}

const styles = {
  // Mobile styles
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-end',
  },
  mobileSheet: {
    width: '100%',
    height: '40vh', // WORLD-CLASS: Initial collapsed height (will be overridden by state)
    maxHeight: '92vh', // WORLD-CLASS: Max expanded height
    minHeight: '35vh', // WORLD-CLASS: Min collapsed height
    backgroundColor: 'var(--color-bg-primary)',
    borderTopLeftRadius: 'var(--radius-xl)',
    borderTopRightRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-2xl)',
    display: 'flex',
    flexDirection: 'column',
    // BEYOND WORLD-CLASS: Respect reduced motion preference (computed at render time)
    transition: typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      ? 'height 0.1s ease-out' 
      : 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // WORLD-CLASS: Smooth easing
    touchAction: 'none', // Prevent default touch behaviors on sheet
    overscrollBehavior: 'contain', // Prevent scroll chaining
    willChange: 'height', // WORLD-CLASS: Optimize for height animations
  },
  handleBar: {
    width: '40px',
    height: '4px',
    backgroundColor: 'var(--color-border-medium)',
    borderRadius: '2px',
    margin: '0 auto var(--spacing-2)',
    flexShrink: 0,
  },
  handleZone: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    minHeight: '56px', // WORLD-CLASS V2: Large tap area (44-56px)
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab',
    touchAction: 'none', // Dedicated drag handle
    userSelect: 'none',
    flexShrink: 0,
  },
  handleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    justifyContent: 'center',
  },
  handleHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    fontWeight: 'var(--font-weight-normal)',
  },
  chevron: {
    fontSize: '16px',
    color: 'var(--color-text-secondary)',
    transition: 'transform 0.3s ease-out',
    lineHeight: 1,
    display: 'inline-block',
  },
  // B1: Peek summary row styles
  peekRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    borderBottom: '1px solid var(--color-border-light)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    minHeight: '44px',
    flexShrink: 0,
  },
  peekLabel: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    flexShrink: 0,
  },
  peekSummary: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  peekChevron: {
    fontSize: '12px',
    color: 'var(--color-text-tertiary)',
    flexShrink: 0,
    transition: 'transform 0.3s ease-out',
  },
  mobileHeaderZone: {
    flexShrink: 0,
    borderBottom: '1px solid var(--color-border-light)',
  },
  mobileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-3) var(--spacing-5) var(--spacing-4)',
    flexShrink: 0,
  },
  mobileTitle: {
    fontSize: 'var(--typography-level-3-size)',
    fontWeight: 'var(--typography-level-3-weight)',
    color: 'var(--typography-level-3-color)',
    margin: 0,
  },
  mobileHeaderCloseOnly: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 'var(--spacing-2) var(--spacing-4) var(--spacing-3)',
    flexShrink: 0,
  },
  mobileCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-2xl)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    padding: 'var(--spacing-1)',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileContent: {
    flex: 1,
    overflow: 'auto',
    overflowX: 'hidden', // FIX: Prevent horizontal scroll
    padding: 'var(--spacing-5)',
    WebkitOverflowScrolling: 'touch', // FIX: Smooth iOS scrolling
    overscrollBehavior: 'contain', // FIX: Prevent scroll chaining
    minHeight: 0, // FIX: Allow flex child to shrink and enable scrolling
    touchAction: 'pan-y', // FIX: Allow vertical scroll in content area
  },
  
  // Desktop styles
  desktopOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 'var(--spacing-4)',
  },
  desktopModal: {
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-xl)',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  },
  desktopHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-5)',
    borderBottom: '1px solid var(--color-border-light)',
  },
  desktopTitle: {
    fontSize: 'var(--typography-level-3-size)',
    fontWeight: 'var(--typography-level-3-weight)',
    color: 'var(--typography-level-3-color)',
    margin: 0,
  },
  desktopCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-2xl)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    padding: 'var(--spacing-1)',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopContent: {
    flex: 1,
    overflow: 'auto',
    padding: 'var(--spacing-5)',
  },
};

